import { eventsManager, metricsManager as globalsMetricsManager } from "../Globals";
import { arrayAverage, buildErrorContext } from "gather-common/dist/src/public/utils";
import { clone, fromPairs, sum, toPairs, uniq } from "ramda";
import { Logger } from "../Logger";
import { calculateConcealedSamplesDuration, calculateFramesUndecoded, collectCodecsFromRtpData, collectMetrics, isRTCAudioSourceStats, isRTCInboundRtpStreamStats, isRTCMediaSourceStats, isRTCOutboundRtpStreamStats, parseRTCStats, } from "./rtcStats";
import { calculateBitrate } from "../utils";
import * as mediasoup from "mediasoup-client";
import { tracker } from "../tracker/Tracker";
import { MetricsEventName } from "gather-http-common/dist/src/public/metrics/constants";
import { GVCInternalEvent } from "../EventEmitter/GVCEventEmitter";
import { Err, Ok } from "gather-common-including-video/dist/src/public/result";
import ipRangeCheck from "ip-range-check";
import { TWILIO_IP_RANGES } from "./TWILIO_IP_RANGES";
const SEND_STATS_INTERVAL_MS = 60000;
const QUERY_STATS_DEFAULT_INTERVAL = 1000;
const BYTES_RECV_IN_CALL_THRESHOLD = 0;
const BYTES_SENT_IN_CALL_THRESHOLD = 5000;
const AUDIO_ACTIVITY_THRESHOLD = 0.01;
const SEVERELY_CONCEALED_THRESHOLD = 0.05;
const BAD_PACKET_LOSS_THRESHOLD = 0.1;
const STANDARD_RESOLUTIONS = new Set([180, 360, 720]);
const DEFAULT_GET_SFU_CLIENTS_FN = () => {
    Logger.warn("RTCStatsManager is not provided with a getSfuClients implementation");
    return {};
};
const DEFAULT_GET_APP_CONTEXT_FN = () => {
    Logger.warn("RTCStatsManager is not provided with a getAppContext implementation");
    return { consumingScreenShareCount: 0 };
};
const ZERO_HIGH_FREQ_STATS = { severelyConcealedSeconds: 0, badPacketLossSeconds: 0 };
export class RTCStatsManager {
    getSfuClients = DEFAULT_GET_SFU_CLIENTS_FN;
    getAppContext = DEFAULT_GET_APP_CONTEXT_FN;
    _eventEmitter;
    _enableProducedAudioLevelTracking = false;
    _interval = QUERY_STATS_DEFAULT_INTERVAL;
    _prevStats;
    _availableRecvBandwidth;
    _sendStatsInterval;
    _queryStatsTimeout;
    highFrequencyStats;
    codecsInUse;
    _stats = {};
    onUpdateMetrics(_sfu, _availableBitrate) { }
    _eventAttributes;
    constructor(eventEmitter) {
        this._eventEmitter = eventEmitter;
        this._sendStatsInterval = -1;
        this._queryStatsTimeout = -1;
        this._prevStats = {};
        this._availableRecvBandwidth = {};
        this._eventAttributes = {};
        this.highFrequencyStats = { ...ZERO_HIGH_FREQ_STATS };
        this.codecsInUse = [];
    }
    setEventAttribute(prefix, name, value) {
        this._eventAttributes[`${prefix}.${name}`] = value;
    }
    destroy() {
        this._disableStatsCollection();
        this.getSfuClients = DEFAULT_GET_SFU_CLIENTS_FN;
        this.getAppContext = DEFAULT_GET_APP_CONTEXT_FN;
        this._stats = {};
        this.codecsInUse = [];
        this.setGlobalMetricAttributes();
    }
    enableProducedAudioLevelTracking(tracking) {
        this._enableProducedAudioLevelTracking = tracking;
    }
    prevPerServerStats = { cache: { send: {}, recv: {} }, send: {}, recv: {} };
    _sendStats = async () => {
        const context = this.getAppContext();
        const sfuClients = Object.values(this.getSfuClients());
        const { totalStats, totalStatsPerKind, perServerStats } = this.calculateBandwidthUsage(this.prevPerServerStats);
        this.prevPerServerStats = perServerStats;
        if ((totalStats.recv.bytesReceived ?? 0) <= BYTES_RECV_IN_CALL_THRESHOLD &&
            (totalStats.send.bytesSent ?? 0) <= BYTES_SENT_IN_CALL_THRESHOLD) {
            return;
        }
        const codecsInUseSend = new Set();
        const codecsInUseRecv = new Set();
        let totalProducingSFUs = 0;
        let totalProducingSFUsHighBitrate = 0;
        let totalConsumingSFUs = 0;
        for (const sfu of sfuClients) {
            const serverURL = sfu.serverURL;
            if (!this._prevStats[serverURL]) {
                this._prevStats[serverURL] = {};
            }
            const sendRtcStats = this.getCachedStats(serverURL, "send");
            if (sendRtcStats) {
                try {
                    const parsedSendStats = parseRTCStats(sendRtcStats);
                    if (parsedSendStats && parsedSendStats["outbound-rtp"]) {
                        const sendMetrics = this._calculateSendMetrics(parsedSendStats, serverURL);
                        const codecsInUse = collectCodecsFromRtpData(parsedSendStats["outbound-rtp"]);
                        codecsInUse.forEach((codec) => codecsInUseSend.add(codec));
                        const turnProviderRes = getTransportTurnProvider(sendRtcStats);
                        const turnProvider = turnProviderRes.ok && turnProviderRes.value.isTurn
                            ? turnProviderRes.value.turnProvider
                            : "none";
                        if (sendMetrics.bitrate > 0) {
                            totalProducingSFUs++;
                        }
                        if (sendMetrics.bitrate > 1000) {
                            totalProducingSFUsHighBitrate++;
                        }
                        const url = new URL(serverURL);
                        const niceURL = `${url.hostname}/${url.pathname}`;
                        globalsMetricsManager.addMetric("av-transport", {
                            ...sendMetrics,
                            codecsInUse: codecsInUse.join(" "),
                            sfu: niceURL,
                            direction: "send",
                            turnProvider,
                        });
                        const prevStats = this._prevStats[serverURL] ?? {};
                        prevStats["send"] = clone(parsedSendStats);
                        this._prevStats[serverURL] = prevStats;
                        const { isLimited, bandwidthLimitedDuration, cpuLimitedDuration, reason, bandwidthLimitedPercentage, cpuLimitedPercentage, } = getQualityLimitation({
                            cpu: sendMetrics.cpuLimitationMax,
                            bandwidth: sendMetrics.bandwidthLimitationMax,
                            interval: SEND_STATS_INTERVAL_MS,
                        });
                        if (isLimited) {
                            this._eventEmitter.publishEvent(GVCInternalEvent.QualityLimitation, {
                                reason,
                                bandwidthLimitedDuration,
                                cpuLimitedDuration,
                                bandwidthLimitedPercentage,
                                cpuLimitedPercentage,
                            });
                        }
                    }
                }
                catch (e) {
                    Logger.error("Failed to process RTCStats", buildErrorContext(e));
                }
            }
            const recvRtcStats = this.getCachedStats(serverURL, "recv");
            if (recvRtcStats) {
                try {
                    const parsedRecvStats = parseRTCStats(recvRtcStats);
                    if (parsedRecvStats && parsedRecvStats["inbound-rtp"]) {
                        totalConsumingSFUs++;
                        const url = new URL(serverURL);
                        const niceURL = `${url.hostname}/${url.pathname}`;
                        const codecsInUse = collectCodecsFromRtpData(parsedRecvStats["inbound-rtp"]);
                        codecsInUse.forEach((codec) => codecsInUseRecv.add(codec));
                        const turnProviderRes = getTransportTurnProvider(recvRtcStats);
                        const turnProvider = turnProviderRes.ok && turnProviderRes.value.isTurn
                            ? turnProviderRes.value.turnProvider
                            : "none";
                        globalsMetricsManager.addMetric("av-transport", {
                            ...this._calculateRecvMetrics(parsedRecvStats, serverURL),
                            codecsInUse: codecsInUse.join(" "),
                            sfu: niceURL,
                            direction: "recv",
                            turnProvider,
                        });
                        const prevStats = this._prevStats[serverURL] ?? {};
                        prevStats["recv"] = parsedRecvStats;
                        this._prevStats[serverURL] = clone(prevStats);
                    }
                }
                catch (e) {
                    Logger.error("Failed to process RTCStats", buildErrorContext(e));
                }
            }
        }
        const concealedSecs = this.highFrequencyStats.severelyConcealedSeconds;
        const packetLossSecs = this.highFrequencyStats.badPacketLossSeconds;
        this.codecsInUse = uniq([...codecsInUseSend.values(), ...codecsInUseRecv.values()]).sort();
        this.setGlobalMetricAttributes();
        this.highFrequencyStats = { ...ZERO_HIGH_FREQ_STATS };
        const payload = {
            ...prefixKey("send", totalStats.send),
            ...prefixKey("recv", totalStats.recv),
            "send.codecsInUse": Array.from(codecsInUseSend).join(" "),
            "recv.codecsInUse": Array.from(codecsInUseRecv).join(" "),
            ...prefixKey("send.audio", totalStatsPerKind.send.audio),
            ...prefixKey("send.video", totalStatsPerKind.send.video),
            ...prefixKey("send.screen", totalStatsPerKind.send.screen),
            ...prefixKey("recv.audio", totalStatsPerKind.recv.audio),
            ...prefixKey("recv.video", totalStatsPerKind.recv.video),
            totalProducingSFUs,
            totalProducingSFUsHighBitrate,
            totalConsumingSFUs,
            severelyConcealedSeconds: concealedSecs,
            badPacketLossSeconds: packetLossSecs,
            ...context,
        };
        globalsMetricsManager.addMetric("av-bandwidth-health", payload);
        tracker.captureMetrics("av-bandwidth-health", payload);
        eventsManager.addEvent(MetricsEventName.AV_METRICS, {
            ...prefixKey("send", totalStats.send),
            ...prefixKey("recv", totalStats.recv),
            totalConsumingSFUs,
            severelyConcealedSeconds: concealedSecs,
            badPacketLossSeconds: packetLossSecs,
            ...this._eventAttributes,
        });
    };
    getSendStats = () => {
        const sfuClients = Object.values(this.getSfuClients());
        const stats = {};
        for (const sfu of sfuClients) {
            const serverURL = sfu.serverURL;
            if (!this._prevStats[serverURL]) {
                this._prevStats[serverURL] = {};
            }
            const stat = this.getCachedStats(serverURL, "send");
            if (stat) {
                stats[serverURL] = parseRTCStats(stat);
            }
        }
        return stats;
    };
    _startSendStatsInterval = () => {
        window.clearInterval(this._sendStatsInterval);
        this._sendStatsInterval = window.setInterval(this._sendStats, SEND_STATS_INTERVAL_MS);
    };
    _startQueryStatsTimeout = () => {
        window.clearTimeout(this._queryStatsTimeout);
        this._queryStatsTimeout = window.setTimeout(this._queryStats, this._interval);
    };
    _queryStats = async () => {
        const sfuClients = Object.values(this.getSfuClients());
        const lastStats = { ...this._stats };
        for (const sfu of sfuClients) {
            const before = new Date().getTime();
            const send = await RTCStatsManager.getStatsObject(sfu.sendTransport);
            const afterSend = new Date().getTime();
            const recv = await RTCStatsManager.getStatsObject(sfu.recvTransport);
            const after = new Date().getTime();
            const mediaSourceMap = {};
            if (send) {
                Object.values(send)
                    .filter((stats) => isRTCMediaSourceStats(stats))
                    .map((stats) => ({ trackIdentifier: "", kind: "", ...stats }))
                    .reduce((map, stats) => {
                    map[stats.id] = stats.trackIdentifier;
                    return map;
                }, mediaSourceMap);
            }
            this._stats[sfu.serverURL] = {
                send,
                mediaSourceMap,
                getSendStatsTime: afterSend - before,
                recv,
                getRecvStatsTime: after - afterSend,
            };
        }
        this.calcHighFrequencyStats(this._stats, lastStats);
        this._startQueryStatsTimeout();
    };
    enableStatsCollection(getClientsHandler, getAppContext) {
        if (getClientsHandler) {
            this.getSfuClients = getClientsHandler;
        }
        if (getAppContext) {
            this.getAppContext = getAppContext;
        }
        this._startQueryStatsTimeout();
        this._startSendStatsInterval();
    }
    _disableStatsCollection() {
        window.clearTimeout(this._queryStatsTimeout);
        window.clearInterval(this._sendStatsInterval);
    }
    setRecvBandwidth(serverURL, availableBandwidth, usedBandwidth) {
        this._availableRecvBandwidth[serverURL] = availableBandwidth;
        this.onUpdateMetrics(serverURL, availableBandwidth);
        this._eventEmitter.publishEvent(GVCInternalEvent.BandwidthUtilization, {
            sfuAddr: serverURL,
            direction: "recv",
            available: availableBandwidth,
            used: usedBandwidth,
        });
    }
    calculateBandwidthUsage(perServerStats = { cache: { send: {}, recv: {} }, send: {}, recv: {} }) {
        return collectMetrics(perServerStats, this._stats);
    }
    static async getStatsObject(transport) {
        const outputStats = {};
        if (!transport || (transport instanceof mediasoup.types.Transport && transport.closed))
            return outputStats;
        let rtcStats;
        try {
            rtcStats = await transport.getStats();
        }
        catch (e) {
            Logger.error("Failed to get stats from transport", buildErrorContext(e));
        }
        rtcStats?.forEach((value, id, _parent) => {
            outputStats[id] = value;
        });
        return outputStats;
    }
    _calculateSendMetrics(stats, serverURL) {
        const outboundRtpStatsMap = stats["outbound-rtp"] ?? {};
        const mediaSourceStatsMap = stats["media-source"];
        const prevMediaSourceStatsMap = this._prevStats[serverURL]?.["send"]?.["media-source"];
        const prevOutboundRtpStatsMap = this._prevStats[serverURL]?.["send"]?.["outbound-rtp"];
        const remoteInboundRtpStatsMap = stats["remote-inbound-rtp"];
        const prevRemoteInboundRtpStatsMap = this._prevStats[serverURL]?.["send"]?.["remote-inbound-rtp"];
        const prevCandidatePairStatsMap = this._prevStats[serverURL]?.["send"]?.["candidate-pair"];
        const packetsLostPercentageArray = [];
        const nackCountArray = [];
        const firPliCountArray = [];
        const jitterArray = [];
        const roundTripTimeArray = [];
        const bandwidthLimitationArray = [];
        const cpuLimitationArray = [];
        const encodeMetricsByRes = {};
        let maxNonStandardEncodedRes;
        let totalEncodeTimeMs = 0;
        Object.values(outboundRtpStatsMap).forEach((outboundRtpStats) => {
            const prevOutboundRtpStats = prevOutboundRtpStatsMap
                ? prevOutboundRtpStatsMap[outboundRtpStats.id]
                : undefined;
            const remoteInboundRtpStats = remoteInboundRtpStatsMap
                ? remoteInboundRtpStatsMap[outboundRtpStats.id]
                : undefined;
            const prevRemoteInboundRtpStats = prevRemoteInboundRtpStatsMap
                ? prevRemoteInboundRtpStatsMap[outboundRtpStats.id]
                : undefined;
            let bandwidthLimitation = outboundRtpStats.qualityLimitationDurations?.bandwidth || 0;
            if (bandwidthLimitation && prevOutboundRtpStats?.qualityLimitationDurations?.bandwidth) {
                bandwidthLimitation -= prevOutboundRtpStats?.qualityLimitationDurations?.bandwidth;
            }
            bandwidthLimitationArray.push(bandwidthLimitation);
            let cpuLimitation = outboundRtpStats.qualityLimitationDurations?.cpu || 0;
            if (cpuLimitation && prevOutboundRtpStats?.qualityLimitationDurations?.cpu) {
                cpuLimitation -= prevOutboundRtpStats?.qualityLimitationDurations?.cpu;
            }
            cpuLimitationArray.push(cpuLimitation);
            let packetsSent = outboundRtpStats.packetsSent || 0;
            if (packetsSent && prevOutboundRtpStats?.packetsSent) {
                packetsSent -= prevOutboundRtpStats.packetsSent;
            }
            let packetsLost = remoteInboundRtpStats?.packetsLost || 0;
            if (packetsLost && prevRemoteInboundRtpStats?.packetsLost) {
                packetsLost -= prevRemoteInboundRtpStats.packetsLost;
            }
            let packetsLostPercentage = 0;
            if (packetsSent > 0) {
                packetsLostPercentage = packetsLost / (packetsSent + packetsLost);
            }
            packetsLostPercentageArray.push(packetsLostPercentage);
            const jitter = remoteInboundRtpStats?.jitter || 0;
            jitterArray.push(jitter);
            let nackCount = outboundRtpStats.nackCount || 0;
            if (prevOutboundRtpStats?.nackCount) {
                if (prevOutboundRtpStats?.nackCount <= nackCount) {
                    nackCount -= prevOutboundRtpStats.nackCount;
                }
            }
            nackCountArray.push(nackCount);
            let firCount = outboundRtpStats.firCount || 0;
            if (prevOutboundRtpStats?.firCount) {
                if (prevOutboundRtpStats.firCount <= firCount) {
                    firCount -= prevOutboundRtpStats.firCount;
                }
            }
            let pliCount = outboundRtpStats.pliCount || 0;
            if (prevOutboundRtpStats?.pliCount) {
                if (prevOutboundRtpStats.pliCount <= pliCount) {
                    pliCount -= prevOutboundRtpStats.pliCount;
                }
            }
            const firPliCount = firCount + pliCount;
            firPliCountArray.push(firPliCount);
            const rtt = remoteInboundRtpStats?.roundTripTime;
            if (rtt) {
                roundTripTimeArray.push(rtt);
            }
            let framesEncoded = outboundRtpStats.framesEncoded || 0;
            if (framesEncoded && prevOutboundRtpStats?.framesEncoded) {
                framesEncoded -= prevOutboundRtpStats.framesEncoded;
            }
            let keyFramesEncoded = outboundRtpStats.keyFramesEncoded || 0;
            if (keyFramesEncoded && prevOutboundRtpStats?.keyFramesEncoded) {
                keyFramesEncoded -= prevOutboundRtpStats.keyFramesEncoded;
            }
            const deltaFramesEncoded = framesEncoded - keyFramesEncoded;
            let totalEncodeTime = outboundRtpStats.totalEncodeTime || 0;
            if (totalEncodeTime && prevOutboundRtpStats?.totalEncodeTime) {
                totalEncodeTime -= prevOutboundRtpStats.totalEncodeTime;
            }
            if (totalEncodeTime > 0) {
                totalEncodeTimeMs += totalEncodeTime * 1000;
            }
            const res = Math.min(outboundRtpStats?.frameWidth ?? 0, outboundRtpStats?.frameHeight ?? 0);
            if (framesEncoded > 0 && res > 0) {
                const isStandardRes = STANDARD_RESOLUTIONS.has(res);
                const key = res > 720 ? "720plus" : isStandardRes ? res : "Other";
                const data = encodeMetricsByRes[key] ?? {
                    totalEncodeS: 0,
                    framesEncoded: 0,
                    keyFramesEncoded: 0,
                    deltaFramesEncoded: 0,
                };
                data.totalEncodeS += totalEncodeTime;
                data.framesEncoded += framesEncoded;
                data.keyFramesEncoded += keyFramesEncoded;
                data.deltaFramesEncoded += deltaFramesEncoded;
                encodeMetricsByRes[key] = data;
                if (!isStandardRes) {
                    maxNonStandardEncodedRes = Math.max(maxNonStandardEncodedRes ?? 0, res);
                }
            }
        });
        let audioEnergyLevel = undefined;
        if (this._enableProducedAudioLevelTracking) {
            const audioStats = mediaSourceStatsMap?.audio;
            const prevAudioStats = prevMediaSourceStatsMap?.audio;
            if (audioStats &&
                isRTCAudioSourceStats(audioStats) &&
                prevAudioStats &&
                isRTCAudioSourceStats(prevAudioStats)) {
                audioEnergyLevel = RTCStatsManager.getAudioEnergyLevel(audioStats.totalAudioEnergy, prevAudioStats.totalAudioEnergy, audioStats.totalSamplesDuration, prevAudioStats.totalSamplesDuration);
            }
        }
        const encodeMetrics = {};
        for (const [key, { totalEncodeS, framesEncoded, keyFramesEncoded, deltaFramesEncoded },] of Object.entries(encodeMetricsByRes)) {
            encodeMetrics[`averageEncodeTime${key}`] = totalEncodeS / framesEncoded;
            encodeMetrics[`totalEncodeMs${key}`] = totalEncodeS * 1000;
            encodeMetrics[`numFramesEncoded${key}`] = framesEncoded;
            encodeMetrics[`numKeyFramesEncoded${key}`] = keyFramesEncoded;
            encodeMetrics[`numDeltaFramesEncoded${key}`] = deltaFramesEncoded;
        }
        const localCandidate = stats["local-candidate"];
        const turn = localCandidate?.candidateType === "relay";
        const protocol = localCandidate?.relayProtocol ?? localCandidate?.protocol;
        const localAddress = getLocalAddress(stats["candidates"] ?? {}, localCandidate);
        const candidatePair = stats["candidate-pair"];
        const { bytesSent, timestamp, availableOutgoingBitrate } = candidatePair ?? {
            bytesReceived: 0,
            timestamp: 0,
            availableOutgoingBitrate: 0,
        };
        const elapsedMs = prevCandidatePairStatsMap
            ? timestamp - prevCandidatePairStatsMap.timestamp
            : undefined;
        const bitrate = calculateBitrate(bytesSent, prevCandidatePairStatsMap?.bytesSent ?? bytesSent, elapsedMs !== undefined ? elapsedMs / 1000 : 1);
        return {
            availableBitrate: availableOutgoingBitrate,
            bitrate,
            packetsLost: arrayAverage(packetsLostPercentageArray),
            jitter: arrayAverage(jitterArray),
            roundTripTime: arrayAverage(roundTripTimeArray),
            nack: arrayAverage(nackCountArray),
            firPli: arrayAverage(firPliCountArray),
            bandwidthLimitation: arrayAverage(bandwidthLimitationArray),
            bandwidthLimitationMax: Math.max(...bandwidthLimitationArray),
            cpuLimitation: arrayAverage(cpuLimitationArray),
            cpuLimitationMax: Math.max(...cpuLimitationArray),
            localAddress,
            turn: turn.toString(),
            protocol,
            ...encodeMetrics,
            maxNonStandardEncodedRes,
            totalEncodeTimeMs,
            elapsedMs,
            audioEnergyLevel,
        };
    }
    _calculateRecvMetrics(stats, serverURL) {
        const inboundRtpStatsMap = stats["inbound-rtp"];
        const prevInboundRtpStatsMap = this._prevStats[serverURL]?.["recv"]?.["inbound-rtp"];
        const prevCandidatePairStatsMap = this._prevStats[serverURL]?.["recv"]?.["candidate-pair"];
        const packetsLostPercentageArray = [];
        const nackCountArray = [];
        const firPliCountArray = [];
        const jitterArray = [];
        const framesUndecodedPercentageArray = [];
        const audioConcealmentSecondsArray = [];
        const audioTotalSecondsArray = [];
        const decodeMetricsByRes = {};
        let maxNonStandardDecodedRes;
        let totalDecodeTimeMs = 0;
        Object.values(inboundRtpStatsMap ?? {}).forEach((inboundRtpStats) => {
            if (inboundRtpStats.ssrc === 1234)
                return;
            const prevInboundRtpStats = prevInboundRtpStatsMap
                ? prevInboundRtpStatsMap[inboundRtpStats.id]
                : undefined;
            let packetsReceived = inboundRtpStats.packetsReceived || 0;
            if (packetsReceived && prevInboundRtpStats?.packetsReceived) {
                packetsReceived -= prevInboundRtpStats.packetsReceived;
            }
            let packetsLost = inboundRtpStats.packetsLost || 0;
            if (packetsLost && prevInboundRtpStats?.packetsLost) {
                packetsLost -= prevInboundRtpStats.packetsLost;
            }
            let packetsLostPercentage = 0;
            if (packetsLost + packetsReceived > 0) {
                packetsLostPercentage = packetsLost / (packetsLost + packetsReceived);
            }
            packetsLostPercentageArray.push(packetsLostPercentage);
            if (inboundRtpStats.totalSamplesReceived !== undefined) {
                const { concealedSamplesDurationS, totalSamplesDurationS } = calculateConcealedSamplesDuration(prevInboundRtpStats, inboundRtpStats);
                audioConcealmentSecondsArray.push(concealedSamplesDurationS);
                audioTotalSecondsArray.push(totalSamplesDurationS);
            }
            framesUndecodedPercentageArray.push(calculateFramesUndecoded(prevInboundRtpStats, inboundRtpStats));
            const jitter = inboundRtpStats.jitter || 0;
            jitterArray.push(jitter);
            let nackCount = inboundRtpStats.nackCount || 0;
            if (prevInboundRtpStats?.nackCount) {
                nackCount -= prevInboundRtpStats.nackCount;
            }
            nackCountArray.push(nackCount);
            let firCount = inboundRtpStats.firCount || 0;
            if (prevInboundRtpStats?.firCount) {
                firCount -= prevInboundRtpStats.firCount;
            }
            let pliCount = inboundRtpStats.pliCount || 0;
            if (prevInboundRtpStats?.pliCount) {
                pliCount -= prevInboundRtpStats.pliCount;
            }
            const firPliCount = firCount + pliCount;
            firPliCountArray.push(firPliCount);
            let framesDecoded = inboundRtpStats.framesDecoded || 0;
            if (framesDecoded && prevInboundRtpStats?.framesDecoded) {
                framesDecoded -= prevInboundRtpStats.framesDecoded;
            }
            let totalDecodeTime = inboundRtpStats.totalDecodeTime || 0;
            if (totalDecodeTime && prevInboundRtpStats?.totalDecodeTime) {
                totalDecodeTime -= prevInboundRtpStats.totalDecodeTime;
            }
            if (totalDecodeTime > 0) {
                totalDecodeTimeMs += totalDecodeTime * 1000;
            }
            let keyFramesDecoded = inboundRtpStats.keyFramesDecoded || 0;
            if (keyFramesDecoded && prevInboundRtpStats?.keyFramesDecoded) {
                keyFramesDecoded -= prevInboundRtpStats.keyFramesDecoded;
            }
            const deltaFramesDecoded = framesDecoded - keyFramesDecoded;
            const res = Math.min(inboundRtpStats?.frameWidth ?? 0, inboundRtpStats?.frameHeight ?? 0);
            if (framesDecoded > 0 && res > 0) {
                const isStandardRes = STANDARD_RESOLUTIONS.has(res);
                const key = res > 720 ? "720plus" : isStandardRes ? res : "Other";
                const data = decodeMetricsByRes[key] ?? {
                    totalDecodeS: 0,
                    framesDecoded: 0,
                    keyFramesDecoded: 0,
                    deltaFramesDecoded: 0,
                };
                data.totalDecodeS += totalDecodeTime;
                data.framesDecoded += framesDecoded;
                data.keyFramesDecoded += keyFramesDecoded;
                data.deltaFramesDecoded += deltaFramesDecoded;
                decodeMetricsByRes[key] = data;
                if (!isStandardRes) {
                    maxNonStandardDecodedRes = Math.max(maxNonStandardDecodedRes ?? 0, res);
                }
            }
        });
        const decodeMetrics = {};
        for (const [key, { totalDecodeS, framesDecoded, keyFramesDecoded, deltaFramesDecoded },] of Object.entries(decodeMetricsByRes)) {
            decodeMetrics[`averageDecodeTime${key}`] = totalDecodeS / framesDecoded;
            decodeMetrics[`totalDecodeMs${key}`] = totalDecodeS * 1000;
            decodeMetrics[`numFramesDecoded${key}`] = framesDecoded;
            decodeMetrics[`numKeyFramesDecoded${key}`] = keyFramesDecoded;
            decodeMetrics[`deltaFramesDecoded${key}`] = deltaFramesDecoded;
        }
        const localCandidate = stats["local-candidate"];
        const turn = localCandidate?.candidateType === "relay";
        const protocol = localCandidate?.relayProtocol ?? localCandidate?.protocol;
        const localAddress = getLocalAddress(stats["candidates"] ?? {}, localCandidate);
        const candidatePair = stats["candidate-pair"];
        const { bytesReceived, timestamp, availableIncomingBitrate, currentRoundTripTime } = candidatePair ?? {
            bytesReceived: 0,
            timestamp: 0,
            availableIncomingBitrate: 0,
            currentRoundTripTime: 0,
        };
        const elapsedMs = prevCandidatePairStatsMap
            ? timestamp - prevCandidatePairStatsMap.timestamp
            : undefined;
        const bitrate = calculateBitrate(bytesReceived, prevCandidatePairStatsMap?.bytesReceived ?? bytesReceived, elapsedMs !== undefined ? elapsedMs / 1000 : 1);
        const availableBitrate = this._availableRecvBandwidth[serverURL]
            ? this._availableRecvBandwidth[serverURL]
            : availableIncomingBitrate;
        return {
            availableBitrate,
            bitrate,
            packetsLost: arrayAverage(packetsLostPercentageArray),
            jitter: arrayAverage(jitterArray),
            roundTripTime: currentRoundTripTime,
            nack: arrayAverage(nackCountArray),
            firPli: arrayAverage(firPliCountArray),
            framesUndecoded: arrayAverage(framesUndecodedPercentageArray),
            localAddress,
            turn: turn.toString(),
            protocol,
            audioConcealmentSeconds: sum(audioConcealmentSecondsArray),
            audioTotalSeconds: sum(audioTotalSecondsArray),
            ...decodeMetrics,
            maxNonStandardDecodedRes,
            totalDecodeTimeMs,
            elapsedMs,
        };
    }
    static getAudioEnergyLevel(energy, prevEnergy, duration, prevDuration) {
        return Math.sqrt((energy - prevEnergy) / (duration - prevDuration));
    }
    static isAudioTrackActive = (stats, prevStats) => {
        if (!stats.totalAudioEnergy || !stats.totalSamplesDuration)
            return false;
        const level = RTCStatsManager.getAudioEnergyLevel(stats.totalAudioEnergy, prevStats?.totalAudioEnergy ?? 0, stats.totalSamplesDuration, prevStats?.totalSamplesDuration ?? 0);
        return level > AUDIO_ACTIVITY_THRESHOLD;
    };
    static getAudioTracks(stats) {
        const inboundTracks = stats["inbound-rtp"];
        if (inboundTracks === undefined)
            return {};
        const audioTracks = Object.entries(inboundTracks).filter(([_id, trackStats]) => trackStats.kind === "audio");
        return Object.fromEntries(audioTracks);
    }
    static isAudioTrackConcealed(trackStats, prevTrackStats) {
        if (!trackStats.concealedSamples || !trackStats.totalSamplesReceived)
            return false;
        const netConcealed = trackStats.concealedSamples - (prevTrackStats?.concealedSamples ?? 0);
        const netReceived = trackStats.totalSamplesReceived - (prevTrackStats?.totalSamplesReceived ?? 0);
        if (netReceived === 0)
            return false;
        const concealedFraction = netConcealed / netReceived;
        return concealedFraction > SEVERELY_CONCEALED_THRESHOLD;
    }
    calcHighFrequencyStats(currentStats, prevStats) {
        const parsedCurrentStats = {};
        const parsedPrevStats = {};
        Object.entries(currentStats).forEach(([sfuAddr, currStats]) => {
            parsedCurrentStats[sfuAddr] = {
                recv: currStats.recv ? parseRTCStats(currStats.recv) : undefined,
                send: currStats.send ? parseRTCStats(currStats.send) : undefined,
            };
            const prevStatsThisSfu = prevStats[sfuAddr];
            if (!prevStatsThisSfu)
                return;
            parsedPrevStats[sfuAddr] = {
                recv: prevStatsThisSfu.recv ? parseRTCStats(prevStatsThisSfu.recv) : undefined,
                send: prevStatsThisSfu.send ? parseRTCStats(prevStatsThisSfu.send) : undefined,
            };
            const { bytesSent: currBytesSent, timestamp: currTimestamp, availableOutgoingBitrate: availableBandwidth, } = parsedCurrentStats[sfuAddr]?.send?.["candidate-pair"] ?? {
                bytesSent: 0,
                timestamp: 0,
                availableOutgoingBitrate: 0,
            };
            const { bytesSent: prevBytesSent, timestamp: prevTimestamp } = parsedPrevStats[sfuAddr]
                ?.send?.["candidate-pair"] ?? {
                bytesSent: 0,
                timestamp: 0,
            };
            const bitrate = Math.round(calculateBitrate(currBytesSent, prevBytesSent, (currTimestamp - prevTimestamp) / 1000));
            this._eventEmitter.publishEvent(GVCInternalEvent.BandwidthUtilization, {
                sfuAddr,
                direction: "send",
                available: availableBandwidth ?? 0,
                used: bitrate,
            });
        });
        const hadConcealedTrack = RTCStatsManager._checkForConcealedAudio(parsedCurrentStats, parsedPrevStats);
        if (hadConcealedTrack) {
            this.highFrequencyStats.severelyConcealedSeconds += this._interval / 1000;
        }
        const lossFraction = RTCStatsManager._calcPacketLossFraction(parsedCurrentStats, parsedPrevStats);
        if (lossFraction >= BAD_PACKET_LOSS_THRESHOLD) {
            this.highFrequencyStats.badPacketLossSeconds += this._interval / 1000;
        }
    }
    static _checkForConcealedAudio(currentStats, prevStats) {
        const hadConcealedTrack = Object.keys(currentStats).filter((sfuAddr) => {
            const recvStats = currentStats[sfuAddr]?.recv;
            if (!recvStats)
                return;
            const prevRecvStats = prevStats[sfuAddr]?.recv;
            const audioTracksById = RTCStatsManager.getAudioTracks(recvStats);
            const prevAudioTracksById = prevRecvStats
                ? RTCStatsManager.getAudioTracks(prevRecvStats)
                : {};
            const activeConcealedTracks = Object.entries(audioTracksById).filter(([id, trackStats]) => {
                const prevTrackStats = prevAudioTracksById[id];
                if (!RTCStatsManager.isAudioTrackActive(trackStats, prevTrackStats))
                    return;
                return RTCStatsManager.isAudioTrackConcealed(trackStats, prevTrackStats);
            });
            return activeConcealedTracks.length > 0;
        });
        return hadConcealedTrack.length > 0;
    }
    static _calcPacketLossFraction(parsedCurrentStats, parsedPrevStats) {
        const sfuPacketStats = Object.keys(parsedCurrentStats).map((sfuAddr) => {
            const recvStats = parsedCurrentStats[sfuAddr]?.recv;
            if (!recvStats)
                return;
            const prevRecvStats = parsedPrevStats[sfuAddr]?.recv;
            const tracksById = recvStats["inbound-rtp"] ?? {};
            const prevTracksById = prevRecvStats?.["inbound-rtp"] ?? {};
            const trackPacketStats = Object.entries(tracksById).map(([id, currStats]) => {
                const currPacketsLost = currStats.packetsLost ?? 0;
                const prevPacketsLost = prevTracksById[id]?.packetsLost ?? 0;
                const intervalPacketsLost = Math.max(currPacketsLost - prevPacketsLost, 0);
                const currPacketsReceived = currStats.packetsReceived ?? 0;
                const prevPacketsReceived = prevTracksById[id]?.packetsReceived ?? 0;
                const intervalPacketsReceived = Math.max(currPacketsReceived - prevPacketsReceived, 0);
                return { packetsLost: intervalPacketsLost, packetsReceived: intervalPacketsReceived };
            });
            const sfuPacketsLost = sum(trackPacketStats.map((stats) => stats.packetsLost));
            const sfuPacketsReceived = sum(trackPacketStats.map((stats) => stats.packetsReceived));
            return { packetsLost: sfuPacketsLost, packetsReceived: sfuPacketsReceived };
        });
        const totalPacketsLost = sum(sfuPacketStats.map((stats) => stats?.packetsLost ?? 0));
        const totalPacketsReceived = sum(sfuPacketStats.map((stats) => stats?.packetsReceived ?? 0));
        const totalPackets = totalPacketsReceived + totalPacketsLost;
        const lossFraction = totalPackets === 0 ? 0 : totalPacketsLost / totalPackets;
        return lossFraction;
    }
    getCachedStats(sfu, direction) {
        return direction === "send"
            ? this._stats[sfu]?.send
            : direction === "recv"
                ? this._stats[sfu]?.recv
                : null;
    }
    getStatsQueryTime(sfu, direction) {
        return direction === "send"
            ? this._stats[sfu]?.getSendStatsTime
            : this._stats[sfu]?.getRecvStatsTime;
    }
    getCachedSendTransceiverStats(sfu, transceiver) {
        const fullStats = this._stats[sfu]?.send;
        const mediaSourceMap = this._stats[sfu]?.mediaSourceMap;
        const kind = transceiver.track?.kind;
        const trackId = transceiver.track?.id;
        if (!fullStats || !mediaSourceMap || !kind || !trackId)
            return [];
        const filteredStats = Object.values(fullStats).filter((stats) => (isRTCOutboundRtpStreamStats(stats) &&
            stats.kind === kind &&
            stats.mediaSourceId &&
            mediaSourceMap[stats.mediaSourceId] === trackId) ||
            (isRTCOutboundRtpStreamStats(stats) && stats.kind === kind && !stats.mediaSourceId) ||
            (isRTCMediaSourceStats(stats) && stats.kind === kind && stats.trackIdentifier === trackId));
        return filteredStats;
    }
    getCachedRecvTransceiverStats(sfu, transceiver) {
        const fullStats = this._stats[sfu]?.recv;
        const kind = transceiver.track?.kind;
        const trackId = transceiver.track?.id;
        if (!fullStats || !kind || !trackId)
            return [];
        const filteredStats = Object.values(fullStats).filter((stats) => isRTCInboundRtpStreamStats(stats) &&
            stats.kind === kind &&
            stats.trackIdentifier === trackId);
        return filteredStats;
    }
    getCachedRecvCandidatePairStats(sfu) {
        const recvRtcStats = this.getCachedStats(sfu, "recv");
        if (recvRtcStats) {
            try {
                const parsedRecvStats = parseRTCStats(recvRtcStats);
                if (parsedRecvStats && parsedRecvStats["candidate-pair"]) {
                    const recvStats = parsedRecvStats["candidate-pair"];
                    return recvStats;
                }
            }
            catch (e) {
                Logger.error("Failed to process RTCStats", buildErrorContext(e));
            }
        }
        return undefined;
    }
    setGlobalMetricAttributes() {
        globalsMetricsManager.setAttribute("codecsInUse", this.codecsInUse.join());
    }
    getRtcConnectionStats(serverURL, direction) {
        const rtcStats = this.getCachedStats(serverURL, direction);
        const outputStats = {};
        if (!rtcStats)
            return outputStats;
        const parsedStats = parseRTCStats(rtcStats);
        if (parsedStats["local-candidate"]) {
            const { address, relay } = getCandidateAddresses(parsedStats["local-candidate"]);
            outputStats["local-candidate"] = address;
            if (relay) {
                outputStats["relay"] = relay;
            }
        }
        if (parsedStats["remote-candidate"]) {
            const { address, relay } = getCandidateAddresses(parsedStats["remote-candidate"]);
            outputStats["remote-candidate"] = address;
            if (relay) {
                outputStats["relay"] = relay;
            }
        }
        return outputStats;
    }
}
export const prefixKey = (prefix, target) => fromPairs(toPairs(target).map(([key, value]) => [`${prefix}.${key}`, value]));
export const getCandidateAddresses = (candidate) => {
    const isHost = candidate.candidateType === "host";
    const isTurn = candidate.candidateType === "relay";
    const localAddress = isHost || isTurn
        ? `${candidate.address}:${candidate.port}/${candidate.protocol}`
        : `${candidate.relatedAddress}:${candidate.relatedPort}/${candidate.protocol}`;
    const relayAddress = isTurn
        ? `${candidate.relatedAddress}:${candidate.relatedPort}/${candidate.relayProtocol}`
        : undefined;
    return {
        address: localAddress,
        relay: relayAddress,
    };
};
const getLocalAddress = (candidates, selectedLocalCandidate) => {
    if (selectedLocalCandidate?.candidateType === "srflx")
        return selectedLocalCandidate.address;
    const address = selectedLocalCandidate?.address;
    const localSrflxCandidate = Object.values(candidates).find((c) => c.type === "local-candidate" &&
        c.candidateType === "srflx" &&
        (!selectedLocalCandidate || c.relatedAddress === selectedLocalCandidate?.address));
    return localSrflxCandidate?.address ?? address;
};
export function getQualityLimitation({ cpu = 0, bandwidth = 0, interval = SEND_STATS_INTERVAL_MS, }) {
    return {
        isLimited: cpu > 0 || bandwidth > 0,
        reason: cpu > 0 ? "cpu" : bandwidth > 0 ? "bandwidth" : "none",
        bandwidthLimitedDuration: bandwidth,
        cpuLimitedDuration: cpu,
        bandwidthLimitedPercentage: bandwidth / (interval / 1000),
        cpuLimitedPercentage: cpu / (interval / 1000),
    };
}
export const getTransportTurnProvider = (transportStats) => {
    const parsed = parseRTCStats(transportStats);
    const localCandidate = parsed["local-candidate"];
    if (!localCandidate)
        return Err(new Error("No local candidate found"));
    const turn = localCandidate?.candidateType === "relay";
    if (!turn)
        return Ok({ isTurn: false });
    const url = localCandidate.url;
    const turnProviderFromUrl = url?.includes("cloudflare")
        ? "cloudflare"
        : url?.includes("twilio")
            ? "twilio"
            : undefined;
    const turnIp = localCandidate.address;
    if (!turnIp)
        return Err(new Error("No TURN IP found"));
    const isTwilioIp = ipRangeCheck(turnIp, TWILIO_IP_RANGES);
    const isCloudflareIp = turnIp && !isTwilioIp;
    const turnProviderFromAddress = isTwilioIp ? "twilio" : isCloudflareIp ? "cloudflare" : undefined;
    const turnProvider = turnProviderFromUrl ?? turnProviderFromAddress;
    if (!turnProvider)
        return Err(new Error("couldn't get TURN provider from transport stats"));
    return Ok({ isTurn: true, turnProvider });
};
//# sourceMappingURL=RTCStatsManager.js.map